<template>
    <UiModalMobile :is-open="isOpen" title="Filters" @close="$emit('close')">
        <template #content>
            <div>
                <!-- User -->
                <div v-if="userOptions.length > 1" class="py-2">
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        User
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="user in userOptions"
                            :key="user.id"
                            class="flex items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                class="rounded text-10x-orange form-checkbox"
                                :checked="isUserSelected(user)"
                                @change="store.toggleInsightsUserFilter(user)"
                            />
                            <div
                                class="flex h-5 w-5 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
                            >
                                <Icon
                                    :icon="
                                        user.is_business ? 'business' : 'user'
                                    "
                                    class="w-3 h-3 text-slate-500 dark:text-slate-400"
                                />
                            </div>
                            <span class="text-slate-700 dark:text-slate-300">{{ user.name }}</span>
                        </label>
                    </div>
                </div>

                <!-- Card -->
                <div class="py-2">
                    <hr v-if="userOptions.length > 1" class="mb-6" />
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Card
                    </h3>
                    <div>
                        <template
                            v-for="user in usersWithCards"
                            :key="user.id"
                        >
                            <div
                                v-if="userOptions.length > 1"
                                class="flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 py-2"
                            >
                                <Icon icon="user" class="w-4 h-4" />
                                {{ user.name }}
                            </div>
                            <label
                                v-for="card in user.cards"
                                :key="`${user.id}-${card.connected_account_id}`"
                                class="flex items-center gap-3 py-3"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange form-checkbox"
                                    :checked="isCardSelected(card)"
                                    @change="
                                        store.toggleInsightsCardFilter({
                                            connected_account_id:
                                                card.connected_account_id,
                                            title: card.title,
                                        })
                                    "
                                />
                                <div class="flex items-center gap-2 min-w-0 flex-1">
                                    <ImageLoader class="shrink-0">
                                        <template #image>
                                            <img
                                                v-if="
                                                    card.card_image?.path &&
                                                    card.card_image?.file_name
                                                "
                                                :src="`${cdnUrl}/${card.card_image.path}/_small/${card.card_image.file_name}`"
                                                :alt="card.title"
                                                class="w-8 h-5 rounded-sm"
                                            />
                                        </template>
                                        <template #error>
                                            <div
                                                class="w-8 h-5 bg-slate-100 dark:bg-slate-700 rounded-sm border border-slate-200 dark:border-slate-700 flex items-center justify-center"
                                            >
                                                <Icon
                                                    icon="credit-card"
                                                    class="w-3 h-3 text-slate-400"
                                                />
                                            </div>
                                        </template>
                                    </ImageLoader>
                                    <span
                                        class="flex items-center min-w-0 text-slate-700 dark:text-slate-300"
                                    >
                                        <span class="truncate">
                                            {{ card.title }}
                                        </span>
                                        <span
                                            v-if="card.last_four"
                                            class="text-slate-500 dark:text-slate-400 text-sm ml-1 shrink-0"
                                        >
                                            ({{ card.last_four }})
                                        </span>
                                    </span>
                                </div>
                            </label>
                        </template>
                    </div>
                </div>

                <!-- Bank -->
                <div v-if="bankOptions.length > 1" class="py-2">
                    <hr class="mb-6" />
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Bank
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="bank in bankOptions"
                            :key="bank.id"
                            class="flex items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                class="rounded text-10x-orange form-checkbox"
                                :checked="isBankSelected(bank)"
                                @change="store.toggleInsightsBankFilter(bank)"
                            />
                            <img
                                v-if="bank.logo_url"
                                :src="bank.logo_url"
                                :alt="bank.name"
                                class="w-5 h-5 rounded-full shrink-0 object-contain"
                            />
                            <span class="text-slate-700 dark:text-slate-300">{{ bank.name }}</span>
                        </label>
                    </div>
                </div>

                <!-- Benefit period -->
                <div v-if="periodOptions.length > 1" class="py-2">
                    <hr class="mb-6" />
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Benefit period
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="period in periodOptions"
                            :key="period.id"
                            class="flex items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                class="rounded text-10x-orange form-checkbox"
                                :checked="isPeriodSelected(period)"
                                @change="
                                    store.toggleInsightsPeriodFilter(period)
                                "
                            />
                            <span class="text-slate-700 dark:text-slate-300">{{ period.name }}</span>
                        </label>
                    </div>
                </div>

                <!-- Usage -->
                <div class="py-2">
                    <hr class="mb-6" />
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Usage
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="option in usageOptions"
                            :key="option.id"
                            class="flex items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                class="rounded text-10x-orange form-checkbox"
                                :checked="isUsageSelected(option)"
                                @change="
                                    store.toggleInsightsUsageFilter(option)
                                "
                            />
                            <span class="text-slate-700 dark:text-slate-300">{{ option.name }}</span>
                        </label>
                    </div>
                </div>

                <!-- Category -->
                <div v-if="categoryOptions.length > 1" class="py-2">
                    <hr class="mb-6" />
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Category
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="category in categoryOptions"
                            :key="category.id"
                            class="flex items-center gap-3"
                        >
                            <input
                                type="checkbox"
                                class="rounded text-10x-orange form-checkbox"
                                :checked="isCategorySelected(category)"
                                @change="
                                    store.toggleInsightsCategoryFilter(category)
                                "
                            />
                            <span class="text-slate-700 dark:text-slate-300">
                                {{ category.name }}
                            </span>
                        </label>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex gap-3">
                <button
                    :disabled="!hasActiveInsightsFilters"
                    class="btn-base btn-white flex-1"
                    @click="store.clearInsightsFilters()"
                >
                    Clear all
                </button>
                <button
                    class="btn-base btn-primary flex-1"
                    @click="$emit('close')"
                >
                    Apply
                </button>
            </div>
        </template>
    </UiModalMobile>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';

defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
});

defineEmits(['close']);

const store = useCardBenefitsStore();
const {
    insightsSelectedCards: selectedCards,
    insightsSelectedBanks: selectedBanks,
    insightsSelectedPeriodTypes: selectedPeriodTypes,
    insightsSelectedUsage: selectedUsage,
    insightsSelectedCategories: selectedCategories,
    insightsSelectedUsers: selectedUsers,
    hasActiveInsightsFilters,
} = storeToRefs(store);

const cdnUrl = computed(() => window.cdnUrl);
const userOptions = computed(() => store.insightsUserOptions);
const usersWithCards = computed(() => store.insightsUsersWithCards || []);
const bankOptions = computed(() => store.insightsBankOptions);
const periodOptions = computed(() => store.insightsPeriodOptions);
const usageOptions = computed(() => store.insightsUsageOptions);
const categoryOptions = computed(() => store.insightsCategoryOptions);

const isUserSelected = (user) =>
    selectedUsers.value.some((u) => u.id === user.id);
const isCardSelected = (card) =>
    selectedCards.value.some(
        (c) => c.connected_account_id === card.connected_account_id
    );
const isBankSelected = (bank) =>
    selectedBanks.value.some((b) => b.id === bank.id);
const isPeriodSelected = (period) =>
    selectedPeriodTypes.value.some((p) => p.id === period.id);
const isUsageSelected = (option) =>
    selectedUsage.value.some((u) => u.id === option.id);
const isCategorySelected = (category) =>
    selectedCategories.value.some((c) => c.id === category.id);
</script>
